import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Content,
  Header,
  Title,
  FilterSection,
  FilterControls,
  Dropdown,
  DropdownSelect,
  DropdownButton,
  DropdownIcon,
  TableContainer,
  TableInner,
  TableHeader,
  HeaderCell,
  TableBody,
  TableRow,
  Cell,
  PostTitle,
  ActionButtons,
  DeleteButton,
  HideButton,
  NoData,
  Loading,
  ErrorMessage,
  RetryButton,
  ModalOverlay,
  DeleteModal,
  DeleteIcon,
  DeleteWarning,
  DeleteForm,
  Optional,
  ReasonTextarea,
  DeleteActions,
  CancelButton,
  ConfirmButton,
} from './PostManagementLayoutStyles';
import { postApi, Post, PostListParams } from '../../../services/admin/postApi';
import { getPostCategories, PostCategory } from '../../../services/admin/postCategoryApi';
import { AdminDateFilterModal } from '../common';
import AdminSearchInput from '../common/AdminSearchInput';
import CommunityTablePagination from './CommunityTablePagination';

const PostManagementComponent: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<PostCategory[]>([]);

  // 필터 상태
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null | undefined>(null);
  const [selectedStatus, setSelectedStatus] = useState(''); // 기본값을 빈 문자열로 변경

  // 날짜 필터 상태 (적용된 날짜)
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');

  // 날짜 모달 상태 (임시 선택 날짜)
  const [showDateModal, setShowDateModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  // 검색 및 페이지네이션
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // 모달 상태
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePostId, setDeletePostId] = useState<number | null>(null);

  // 초기 로딩 여부 추적 (첫 번째 fetch 완료 전까지만 true)
  const isFirstLoad = useRef(true);

  // 날짜 형식 검증 함수
  const isValidDateFormat = useCallback((dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) return false;

    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
  }, []);

  // 날짜 범위 유효성 검증 함수
  const validateDateRange = useCallback((fromDate: string, toDate: string): boolean => {
    try {
      // 둘 다 비어있으면 유효
      if (!fromDate && !toDate) return true;

      // 하나만 있으면 유효
      if (!fromDate || !toDate) return true;

      // 둘 다 유효한 날짜 형식인지 확인
      if (!isValidDateFormat(fromDate) || !isValidDateFormat(toDate)) {
        /// console.error('유효하지 않은 날짜 형식:', { fromDate, toDate });
        return false;
      }

      const fromDateObj = new Date(fromDate);
      const toDateObj = new Date(toDate);

      // from이 to보다 뒤에 있는지 확인
      if (fromDateObj > toDateObj) {
        /// console.error('시작 날짜가 종료 날짜보다 뒤에 있습니다:', { fromDate, toDate });
        return false;
      }

      return true;
    } catch (error) {
      /// console.error('날짜 범위 검증 중 오류 발생:', error, { fromDate, toDate });
      return false;
    }
  }, [isValidDateFormat]);

  // 카테고리 목록 조회
  const fetchCategories = useCallback(async () => {
    try {
      const response = await getPostCategories();
      setCategories(response.data);

      // 기본 카테고리 선택 제거 - 사용자가 직접 선택하도록 함
    } catch (err) {
      /// console.error('카테고리 목록 조회 실패:', err);
    }
  }, []);

  // 게시글 목록 조회
  const fetchPosts = useCallback(async (searchTerm?: string) => {
    try {
      setLoading(true);
      setError(null);

      // 검색어는 매개변수로 받거나 현재 상태 사용
      const currentSearchQuery = searchTerm !== undefined ? searchTerm : searchQuery;

      // 날짜 범위 유효성 검증
      if (!validateDateRange(appliedStartDate, appliedEndDate)) {
        /// console.error('유효하지 않은 날짜 범위로 인해 날짜 필터를 초기화합니다:', { fromDate: appliedStartDate, toDate: appliedEndDate });
        alert('유효하지 않은 날짜 범위입니다. 전체 기간으로 초기화됩니다.');
        // 날짜 필터 초기화
        setAppliedStartDate('');
        setAppliedEndDate('');
        setTempStartDate('');
        setTempEndDate('');

        // 초기화된 상태로 다시 호출
        const params: PostListParams = {
          page: currentPage - 1,
          size: 10,
          postCategoryId: selectedCategoryId || null,
          from: undefined,
          to: undefined,
          status: selectedStatus === 'true' ? 'visible' : selectedStatus === 'false' ? 'hidden' : undefined,
          search: currentSearchQuery || undefined
        };

        const response = await postApi.getPosts(params);

        setPosts(response.content);
        setTotalPages(response.totalPages);
        setTotalElements(response.totalElements);
        return;
      }

      const params: PostListParams = {
        page: currentPage - 1, // 0-based indexing
        size: 10,
        postCategoryId: selectedCategoryId || null, // undefined 대신 null로 명시적 설정
        from: appliedStartDate || undefined,
        to: appliedEndDate || undefined,
        status: selectedStatus === 'true' ? 'visible' : selectedStatus === 'false' ? 'hidden' : undefined,
        search: currentSearchQuery || undefined
      };

      const response = await postApi.getPosts(params);

      setPosts(response.content);
      setTotalPages(response.totalPages);
      setTotalElements(response.totalElements);
    } catch (err: any) {
      setError(err.message || '게시글 목록을 불러오는데 실패했습니다.');
      /// console.error('Error fetching posts:', err);
    } finally {
      isFirstLoad.current = false;
      setLoading(false);
    }
  }, [currentPage, selectedCategoryId, appliedStartDate, appliedEndDate, selectedStatus, searchQuery, validateDateRange]);

  // 컴포넌트 마운트 시 데이터 로드
  useEffect(() => {
    fetchCategories();

    // 날짜 기본값을 전체 기간으로 설정 (빈 문자열로 설정)
    setAppliedStartDate('');
    setAppliedEndDate('');
    setTempStartDate('');
    setTempEndDate('');
  }, [fetchCategories]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  // 검색 실행
  const handleSearch = () => {
    setCurrentPage(1);
    fetchPosts(searchQuery);
  };

  // 엔터키 검색
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 삭제 처리
  const handleDeleteClick = (postId: number) => {
    setDeletePostId(postId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async (reason?: string) => {
    if (deletePostId) {
      try {
        await postApi.deletePost(deletePostId, reason);
        setShowDeleteModal(false);
        setDeletePostId(null);
        await fetchPosts();
      } catch (err: any) {
        setError(err.message || '게시글 삭제에 실패했습니다.');
        /// console.error('Error deleting post:', err);
      }
    }
  };

  // 숨김/표시 토글
  const handleHideToggle = async (postId: number) => {
    try {
      // 현재 게시글의 상태를 찾아서 반대로 설정
      const post = posts.find(p => p.id === postId);
      if (post) {
        const newVisible = !post.visible; // 현재 visible 상태의 반대로 설정
        await postApi.togglePostVisibility(postId, newVisible);
        await fetchPosts();
      }
    } catch (err: any) {
      setError(err.message || '게시글 상태 변경에 실패했습니다.');
      /// console.error('Error toggling post visibility:', err);
    }
  };

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    navigate(`/community/post/${postId}`);
  };

  // HTML 태그 제거 함수
  const stripHtmlTags = (html: string): string => {
    if (!html) return '';
    return html.replace(/<[^>]*>/g, '').trim();
  };

  // 페이지네이션 렌더링

  const isInitialLoading = loading && isFirstLoad.current;
  if (isInitialLoading) {
    return (
      <Container>
        <Header>
          <Title>일반 게시글 관리</Title>
        </Header>
        <Content>
          <Loading>로딩 중...</Loading>
        </Content>
      </Container>
    );
  }

  if (error && posts.length === 0) {
    return (
      <Container>
        <Header>
          <Title>일반 게시글 관리</Title>
        </Header>
        <Content>
          <ErrorMessage>
            {error}
            <RetryButton onClick={() => fetchPosts()}>
              다시 시도
            </RetryButton>
          </ErrorMessage>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>일반 게시글 관리</Title>
      </Header>

      <Content>
        {/* 필터 및 검색 섹션 */}
        <FilterSection>
          <FilterControls>
            {/* 카테고리 드롭다운 */}
            <Dropdown>
              <DropdownSelect
                value={selectedCategoryId || ''}
                onChange={(e) => setSelectedCategoryId(e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">카테고리 선택</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </DropdownSelect>
              <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </DropdownIcon>
            </Dropdown>

            {/* 작성일 버튼 */}
            <Dropdown>
              <DropdownButton
                onClick={() => {
                  setShowDateModal(true);
                  // 모달 열 때 현재 적용된 날짜를 임시 날짜로 설정
                  setTempStartDate(appliedStartDate);
                  setTempEndDate(appliedEndDate);
                }}
              >
                {appliedStartDate && appliedEndDate ? `작성일: ${appliedStartDate} ~ ${appliedEndDate}` :
                 appliedStartDate ? `작성일: ${appliedStartDate}` : '작성일'}
              </DropdownButton>
              <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </DropdownIcon>
            </Dropdown>

            {/* 상태 드롭다운 */}
            <Dropdown>
              <DropdownSelect
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <option value="">상태 선택</option>
                <option value="true">표시됨</option>
                <option value="false">숨김</option>
              </DropdownSelect>
              <DropdownIcon width="14" height="14" viewBox="0 0 14 14">
                <path d="M4.17 5.25L7 8.08L9.83 5.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </DropdownIcon>
            </Dropdown>
          </FilterControls>

          {/* // 검색 입력
          <SearchBox>
            <div>
              <div>
                <img
                  src="/images/header/search.svg"
                  alt="검색"
                />
              </div>
              <div>
                <SearchInput
                  type="text"
                  placeholder="검색어를 입력해주세요."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                />
              </div>
            </div>
          </SearchBox> */}
          <AdminSearchInput
            value={searchQuery}
            onChange={(value) => {
              setSearchQuery(value);
              setCurrentPage(1);
            }}
            placeholder="게시글 제목을 입력해주세요."
          />
        </FilterSection>

        {/* 테이블 */}
        <TableContainer>
          <TableInner>
          <TableHeader $columns="60px minmax(200px, 4fr) minmax(64px, 1fr) minmax(50px, 1fr) 90px 130px">
            <HeaderCell>연번</HeaderCell>
            <HeaderCell>제목</HeaderCell>
            <HeaderCell>카테고리</HeaderCell>
            <HeaderCell>작성자</HeaderCell>
            <HeaderCell>작성일</HeaderCell>
            <HeaderCell>관리</HeaderCell>
          </TableHeader>

          <TableBody>
            {posts.length > 0 ? (
              posts.map((post, index) => (
                <TableRow key={post.id} $columns="60px minmax(200px, 4fr) minmax(64px, 1fr) minmax(50px, 1fr) 90px 130px">
                  <Cell>
                    {totalElements - (currentPage - 1) * 10 - index}
                  </Cell>
                  <Cell>
                    <PostTitle
                      title={stripHtmlTags(post.title)}
                      onClick={() => handlePostClick(post.id)}
                      style={{ cursor: 'pointer', color: 'var(--Primary-Normal, #448181)' }}
                    >
                      {stripHtmlTags(post.title)}
                    </PostTitle>
                  </Cell>
                  <Cell>
                    {post.category}
                  </Cell>
                  <Cell>
                    {post.author}
                  </Cell>
                  <Cell>
                    {new Date(post.createdAt).toLocaleDateString('ko-KR').replace(/\./g, '.').replace(/\s/g, '')}
                  </Cell>
                  <Cell>
                    <ActionButtons>
                      <DeleteButton
                        onClick={() => handleDeleteClick(post.id)}
                      >
                        삭제
                      </DeleteButton>
                      <HideButton
                        onClick={() => handleHideToggle(post.id)}
                      >
                        {post.visible ? '숨기기' : '숨김 취소'}
                      </HideButton>
                    </ActionButtons>
                  </Cell>
                </TableRow>
              ))
            ) : (
              <NoData>
                등록된 게시글이 없습니다.
              </NoData>
            )}
          </TableBody>
          </TableInner>
          <CommunityTablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </TableContainer>

        {/* 페이지네이션 */}
      </Content>

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <DeleteConfirmModal
          onConfirm={handleDeleteConfirm}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeletePostId(null);
          }}
        />
      )}

      {/* 날짜 선택 모달 */}
      {showDateModal && (
        <AdminDateFilterModal
          title="작성일 선택"
          startDate={tempStartDate}
          endDate={tempEndDate}
          onStartDateChange={(date) => setTempStartDate(date)}
          onEndDateChange={(date) => setTempEndDate(date)}
          onConfirm={() => {
            // 적용 버튼을 눌렀을 때만 실제 날짜 필터를 적용
            setAppliedStartDate(tempStartDate);
            setAppliedEndDate(tempEndDate);
            setShowDateModal(false);
            setCurrentPage(1);
          }}
          onCancel={() => {
            // 취소 시 임시 날짜를 원래 적용된 날짜로 되돌림
            setTempStartDate(appliedStartDate);
            setTempEndDate(appliedEndDate);
            setShowDateModal(false);
          }}
        />
      )}
    </Container>
  );
};

// 삭제 확인 모달 컴포넌트
interface DeleteConfirmModalProps {
  onConfirm: (reason?: string) => void;
  onCancel: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  onConfirm,
  onCancel
}) => {
  const [reasonText, setReasonText] = useState('');
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm(reasonText);
    } catch (error) {
      /// console.error('Failed to delete post:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <ModalOverlay onClick={onCancel}>
      <DeleteModal onClick={(e) => e.stopPropagation()}>
        <DeleteIcon>⚠️</DeleteIcon>
        <h3>게시글을 삭제하시겠습니까?</h3>
        <DeleteWarning>
          삭제된 게시글은 복구할 수 없습니다. 신중하게 결정해주세요.
        </DeleteWarning>

        <DeleteForm>
          <label>삭제 사유 <Optional>(선택사항)</Optional></label>
          <ReasonTextarea
            placeholder="삭제 사유를 입력해주세요"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            rows={3}
          />
        </DeleteForm>

        <DeleteActions>
          <CancelButton
            onClick={onCancel}
            disabled={isConfirming}
          >
            취소
          </CancelButton>
          <ConfirmButton
            onClick={handleConfirm}
            disabled={isConfirming}
          >
            {isConfirming ? '삭제 중...' : '삭제'}
          </ConfirmButton>
        </DeleteActions>
      </DeleteModal>
    </ModalOverlay>
  );
};

export default PostManagementComponent;
